import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, money, useQuery } from '../api.js';
import { useSession } from '../session.js';
import { Drawer, FindingList, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { CloseOut } from './CloseOut.js';
import { History } from './History.js';
import type { Finding, Reservation } from '../../../shared/types.js';

type Detail = Reservation & { findings: Finding[] };

/**
 * Everything you can do to one reservation: confirm it, move it, fly it, close
 * it out to an invoice, or cancel it — with the findings that apply in view.
 */
export function ReservationDrawer({ id, onClose, onChanged }: {
  id: number; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const { can } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQuery<Detail>(`/schedule/reservations/${id}`);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hobbsOut, setHobbsOut] = useState('');
  const [authorizing, setAuthorizing] = useState(false);

  const r = query.data;

  const act = async (fn: () => Promise<unknown>, okMessage: string) => {
    setBusy(true);
    try {
      await fn();
      toast.ok(okMessage);
      query.reload();
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('Blocked by a conflict', err.findings.map((f) => f.title).join(' · '));
      } else {
        toast.error('That did not work', err instanceof Error ? err.message : undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  const footer = r && (
    <>
      {r.status === 'requested' && can('reservation:approve') && (
        <>
          <button className="btn btn--primary" disabled={busy}
            onClick={() => act(() => api.post(`/schedule/reservations/${id}/status`, { status: 'confirmed' }), 'Reservation confirmed')}>
            Confirm
          </button>
          <button className="btn" disabled={busy}
            onClick={() => act(() => api.post(`/schedule/reservations/${id}/status`, { status: 'declined', reason: 'Declined by the school' }), 'Request declined')}>
            Decline
          </button>
        </>
      )}
      {(r.status === 'confirmed' || r.status === 'held') && can('reservation:move') && (
        <button className="btn btn--primary" disabled={busy} onClick={() => setAuthorizing(true)}>
          Authorize session
        </button>
      )}
      {r.status === 'authorized' && (
        <>
          <button className="btn btn--primary" disabled={busy} onClick={() => setClosing(true)}>
            Close out the lesson
          </button>
          <button className="btn" disabled={busy}
            onClick={() => act(() => api.post(`/schedule/reservations/${id}/unauthorize`), 'Authorization withdrawn')}>
            Withdraw
          </button>
        </>
      )}
      {r.invoiceId && (
        <button className="btn" onClick={() => { onClose(); navigate('/billing'); }}>View invoice</button>
      )}
      {r.status !== 'completed' && r.status !== 'cancelled' && can('reservation:cancel') && (
        <button className="btn btn--danger" disabled={busy}
          onClick={() => act(() => api.post(`/schedule/reservations/${id}/status`, { status: 'cancelled', reason: 'Cancelled from the schedule' }), 'Reservation cancelled')}>
          Cancel
        </button>
      )}
    </>
  );

  return (
    <Drawer
      title={r ? (r.studentName || r.notes || `${r.kind} reservation`) : 'Reservation'}
      subtitle={r ? `${dateLabel(r.startsAt)} · ${time(r.startsAt)}–${time(r.endsAt)}` : undefined}
      onClose={onClose}
      footer={footer}
    >
      {query.loading && !r && <Spinner />}
      {query.error && <div className="empty">{query.error}</div>}
      {r && (
        <Panes>
          <Pane title="Reservation" flush>
            <div className="row" style={{ gap: 6, padding: '12px 24px 10px' }}>
              <Pill>{r.status}</Pill>
              <Pill tone="blue">{r.kind}</Pill>
            </div>
            <div className="kv"><span>Aircraft</span><span>{r.aircraftTail}{r.aircraftModel && ` · ${r.aircraftModel}`}</span></div>
            <div className="kv"><span>Instructor</span><span>{r.instructorName || '—'}</span></div>
            <div className="kv">
              <span>Student</span>
              <span>
                {r.studentName || '—'}
                {/* Whether they have turned up is a fact about the lesson, and
                    the desk should not have to ring the ramp to learn it. */}
                {r.checkedInAt && (
                  <span style={{ color: 'var(--sys-green)', fontWeight: 600 }}>
                    {' · checked in '}{time(r.checkedInAt)}
                  </span>
                )}
              </span>
            </div>
            <div className="kv"><span>Lesson</span><span>{r.lessonLabel || '—'}</span></div>
            <div className="kv">
              <span>Block</span>
              <span>{((r.endsAt - r.startsAt) / 3600000).toFixed(1)} hrs + {r.groundHours.toFixed(1)} ground</span>
            </div>
            {r.hobbsStart != null && (
              <div className="kv">
                <span>Hobbs</span>
                <span>
                  {r.hobbsStart.toFixed(1)}
                  {r.hobbsEnd != null
                    ? ` → ${r.hobbsEnd.toFixed(1)} · ${(r.hobbsEnd - r.hobbsStart).toFixed(1)} hrs`
                    : ' · authorized, not yet closed out'}
                </span>
              </div>
            )}
            {r.holdExpiresAt && (
              <div className="kv"><span>Hold expires</span><span>{time(r.holdExpiresAt)} {dateLabel(r.holdExpiresAt)}</span></div>
            )}
            {r.notes && (
              <div className="kv"><span>Notes</span><span style={{ fontWeight: 400 }}>{r.notes}</span></div>
            )}
            <div className="kv"><span>Booked by</span><span>{r.createdByName || '—'}</span></div>
          </Pane>

          <Pane title={`Needs review · ${r.findings.length}`} flush>
            <FindingList findings={r.findings} onAction={(href) => { onClose(); navigate(href); }} />
          </Pane>

          {r.invoiceId && (
            <Pane title="Invoice" flush>
              <InvoiceSummary invoiceId={r.invoiceId} />
            </Pane>
          )}

          {/* Booked, moved, authorized, closed out — the argument about what was
              agreed, settled from the record rather than from memory. */}
          <Pane title="History">
            <History
              compact
              entity="reservation"
              entityId={r.id}
              limit={8}
              emptyNote="Nothing beyond the booking itself."
            />
          </Pane>
        </Panes>
      )}

      {authorizing && r && (
        <AuthorizeSheet
          reservation={r}
          hobbsOut={hobbsOut}
          setHobbsOut={setHobbsOut}
          onClose={() => setAuthorizing(false)}
          onDone={() => { setAuthorizing(false); query.reload(); onChanged(); }}
        />
      )}
      {closing && r && (
        <CloseOut
          id={id}
          onClose={() => setClosing(false)}
          onDone={() => { setClosing(false); query.reload(); onChanged(); }}
        />
      )}
    </Drawer>
  );
}

/**
 * Authorizing releases the session to fly. The Hobbs on the panel is recorded
 * now, because it is the figure the close-out measures the flight against.
 */
function AuthorizeSheet({ reservation, hobbsOut, setHobbsOut, onClose, onDone }: {
  reservation: Detail;
  hobbsOut: string;
  setHobbsOut: (v: string) => void;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const conflicts = reservation.findings.filter((f) => f.severity === 'conflict');
  const isGround = !reservation.aircraftId;

  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/schedule/reservations/${reservation.id}/authorize`,
        hobbsOut ? { hobbsStart: Number(hobbsOut) } : {});
      toast.ok('Session authorized', isGround ? 'Ground instruction released.' : 'Hobbs out recorded.');
      onDone();
    } catch (err) {
      toast.error('Could not authorize', err instanceof Error ? err.message : undefined);
      setBusy(false);
    }
  };

  return (
    <Drawer
      narrow
      title="Authorize the session"
      subtitle={`${reservation.studentName || reservation.kind} · ${reservation.aircraftTail}`}
      onClose={onClose}
      footer={<button className="btn btn--primary btn--block" disabled={busy} onClick={go}>
        {busy ? 'Authorizing…' : 'Authorize'}
      </button>}
    >
      <div className="card__body stack">
        {conflicts.length > 0 && (
          <div className="auth__error" role="alert">
            <span>
              <span style={{ display: 'block', fontWeight: 600 }}>
                {conflicts.length} item{conflicts.length === 1 ? '' : 's'} still need review
              </span>
              {conflicts.map((c) => <span key={c.code} style={{ display: 'block' }}>{c.title}</span>)}
              <span style={{ display: 'block', marginTop: 4 }}>
                Authorizing does not clear them. The pilot in command decides.
              </span>
            </span>
          </div>
        )}

        {!isGround && (
          <label className="field">
            <span>Hobbs on the panel</span>
            <input className="input closeout__num" inputMode="decimal" autoFocus value={hobbsOut}
              placeholder="Reading from the aircraft record"
              onChange={(e) => setHobbsOut(e.target.value)} />
            <span className="closeout__hint">
              Leave it blank to use the last recorded reading for {reservation.aircraftTail}.
            </span>
          </label>
        )}

        <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
          Nothing is logged or billed yet. When the lesson is over you close it out, enter the
          Hobbs at shutdown, and that is what fills the logbook and the invoice.
        </p>
      </div>
    </Drawer>
  );
}

function InvoiceSummary({ invoiceId }: { invoiceId: number }): JSX.Element | null {
  const { data } = useQuery<{ number: string; totalCents: number; status: string; lines?: { id: number; description: string; amountCents: number }[] }>(
    `/billing/${invoiceId}`,
  );
  if (!data) return null;
  return (
    <>
      <div className="row" style={{ gap: 8, padding: '12px 24px 10px' }}>
        <strong>{data.number}</strong>
        <Pill>{data.status}</Pill>
      </div>
      {data.lines?.map((l) => (
        <div className="kv" key={l.id}>
          <span style={{ fontWeight: 400, textAlign: 'left' }}>{l.description}</span>
          <span>{money(l.amountCents)}</span>
        </div>
      ))}
      <div className="kv"><span>Total</span><span style={{ fontWeight: 700 }}>{money(data.totalCents)}</span></div>
    </>
  );
}

function time(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
function dateLabel(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' });
}
